import type { QueryClient } from '@tanstack/react-query';
import type { Session } from '@/api/client';
import {
  belongsToReaderPlannedOtherCache,
  sortSessionsByDateStartAsc,
} from '@/utils/projectRecordingSessions';
import { removeFromReaderPlannedPagesCache, upsertReaderPlannedPagesCache } from './readerPlannedPagesCache';

export const READER_PLANNED_SESSIONS_QUERY_KEY = 'readerPlannedSessions' as const;

export function readerPlannedSessionsQueryKey(
  readerIds: readonly string[],
  excludeProjectId: string
): readonly [typeof READER_PLANNED_SESSIONS_QUERY_KEY, string, string] {
  const readerIdsKey = [...readerIds].filter(Boolean).sort().join(',');
  return [READER_PLANNED_SESSIONS_QUERY_KEY, readerIdsKey, excludeProjectId] as const;
}

export function parseReaderPlannedSessionsQueryKey(
  queryKey: readonly unknown[]
): { readerIds: string[]; excludeProjectId: string } | null {
  if (queryKey[0] !== READER_PLANNED_SESSIONS_QUERY_KEY) return null;
  const readerIdsKey = queryKey[1];
  const excludeProjectId = queryKey[2];
  if (typeof readerIdsKey !== 'string' || typeof excludeProjectId !== 'string') return null;
  const readerIds = readerIdsKey ? readerIdsKey.split(',').filter(Boolean) : [];
  return { readerIds, excludeProjectId };
}

export function upsertReaderPlannedSessionsCache(queryClient: QueryClient, session: Session): void {
  const entries = queryClient.getQueriesData<Session[]>({ queryKey: [READER_PLANNED_SESSIONS_QUERY_KEY] });
  for (const [queryKey, old] of entries) {
    if (!old) continue;
    const parsed = parseReaderPlannedSessionsQueryKey(queryKey as unknown[]);
    if (!parsed) continue;
    const without = old.filter((item) => item.id !== session.id);
    const belongs = belongsToReaderPlannedOtherCache(session, parsed.readerIds, parsed.excludeProjectId);
    const next = belongs ? sortSessionsByDateStartAsc([...without, session]) : without;
    const unchanged = old.length === next.length && old.every((item, i) => item === next[i]);
    if (!unchanged) {
      queryClient.setQueryData<Session[]>(queryKey, next);
    }
  }
  // Порции «В других проектах» нового дизайна — те же события
  upsertReaderPlannedPagesCache(queryClient, session);
}

export function removeSessionFromReaderPlannedCaches(queryClient: QueryClient, sessionId: string): void {
  const entries = queryClient.getQueriesData<Session[]>({ queryKey: [READER_PLANNED_SESSIONS_QUERY_KEY] });
  for (const [queryKey, old] of entries) {
    if (!old) continue;
    const next = old.filter((s) => s.id !== sessionId);
    if (next.length !== old.length) {
      queryClient.setQueryData<Session[]>(queryKey, next);
    }
  }
  removeFromReaderPlannedPagesCache(queryClient, sessionId);
}
